import type {
  GoogleMaxBatch,
  GoogleMaxBatchCreateRequest,
  GoogleMaxBatchItem,
  GoogleMaxBatchResponse,
} from "./types";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Backend-aligned reliability defaults used by the built-in quick mode. */
export const GOOGLE_MAX_QUICK_DEFAULTS = Object.freeze({
  init_attempts: 3,
  network_attempts: 3,
  min_quota: 2,
});

export interface GoogleMaxBatchFormValues {
  accounts: string;
  proxy_url: string;
  local_proxy_url?: string;
  concurrency: number;
  init_attempts?: number;
  network_attempts?: number;
  min_quota?: number;
}

export function buildGoogleMaxBatchRequest(values: GoogleMaxBatchFormValues): {
  payload: GoogleMaxBatchCreateRequest;
  accountCount: number;
} {
  const rows = values.accounts.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!rows.length) throw new Error("至少粘贴一个 Google 账号");
  if (rows.length > 500) throw new Error("单个批次最多提交 500 个账号");

  const identities = rows.map((row, index) => parseCredentialRow(row, index + 1));
  if (new Set(identities).size !== identities.length) throw new Error("批次中存在重复的 Google 账号");

  const proxyUrl = values.proxy_url.trim();
  if (!proxyUrl) throw new Error("请输入本批次使用的代理地址");
  validateProxyUrl(proxyUrl, "账号代理地址");
  const localProxyUrl = values.local_proxy_url?.trim() || "";
  if (localProxyUrl) validateLocalProxyUrl(localProxyUrl);

  return {
    payload: {
      // Keep the credential block opaque; parsing and encryption belong to the
      // backend, and the browser drops this value immediately after submit.
      accounts: values.accounts,
      proxy_url: proxyUrl,
      ...(localProxyUrl ? { local_proxy_url: localProxyUrl } : {}),
      concurrency: integerInRange(values.concurrency, "并发数", 1, 10),
      init_attempts: integerInRange(values.init_attempts ?? GOOGLE_MAX_QUICK_DEFAULTS.init_attempts, "初始化尝试次数", 1, 3),
      network_attempts: integerInRange(values.network_attempts ?? GOOGLE_MAX_QUICK_DEFAULTS.network_attempts, "网络尝试次数", 1, 3),
      min_quota: integerInRange(values.min_quota ?? GOOGLE_MAX_QUICK_DEFAULTS.min_quota, "最低 Max 免费额度", 1, 100),
    },
    accountCount: rows.length,
  };
}

function parseCredentialRow(row: string, rowNumber: number): string {
  const pipeSeparator = row.indexOf("|");
  const dashSeparator = row.indexOf("---");
  const credential = pipeSeparator > 0 && (dashSeparator < 0 || pipeSeparator < dashSeparator)
    ? parsePipeCredential(row)
    : parseTripleDashCredential(row);
  if (!credential) {
    throw new Error(`第 ${rowNumber} 行格式不正确，请使用 email|password、email|password|recovery_email 或 email---password---recovery_email`);
  }
  const { email, password, recoveryEmail } = credential;
  if (!EMAIL_PATTERN.test(email)) throw new Error(`第 ${rowNumber} 行登录邮箱格式不正确`);
  if (!password || password.length > 1024 || /[\x00-\x1f\x7f]/.test(password)) {
    throw new Error(`第 ${rowNumber} 行 Google 密码格式不正确`);
  }
  if (recoveryEmail !== null && !EMAIL_PATTERN.test(recoveryEmail)) {
    throw new Error(`第 ${rowNumber} 行恢复邮箱格式不正确`);
  }
  return email.toLowerCase();
}

function parsePipeCredential(row: string): { email: string; password: string; recoveryEmail: string | null } | null {
  const firstSeparator = row.indexOf("|");
  if (firstSeparator <= 0) return null;
  const lastSeparator = row.lastIndexOf("|");
  const recoveryCandidate = lastSeparator > firstSeparator ? row.slice(lastSeparator + 1).trim() : "";
  const hasRecoveryEmail = EMAIL_PATTERN.test(recoveryCandidate);
  return {
    email: row.slice(0, firstSeparator).trim(),
    password: row.slice(firstSeparator + 1, hasRecoveryEmail ? lastSeparator : undefined).trim(),
    recoveryEmail: hasRecoveryEmail ? recoveryCandidate : null,
  };
}

function parseTripleDashCredential(row: string): { email: string; password: string; recoveryEmail: string | null } | null {
  const firstSeparator = row.indexOf("---");
  const lastSeparator = row.lastIndexOf("---");
  if (firstSeparator <= 0 || lastSeparator <= firstSeparator) return null;
  return {
    email: row.slice(0, firstSeparator).trim(),
    password: row.slice(firstSeparator + 3, lastSeparator).trim(),
    recoveryEmail: row.slice(lastSeparator + 3).trim(),
  };
}

function validateProxyUrl(value: string, label: string): void {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${label}格式不正确`);
  }
  if (!["http:", "https:", "socks5:", "socks5h:"].includes(url.protocol) || !url.hostname || !url.port) {
    throw new Error(`${label}需包含协议、主机和端口`);
  }
}

function validateLocalProxyUrl(value: string): void {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("本机中转代理地址格式不正确");
  }
  const hostname = url.hostname.toLowerCase().replace(/^\[|\]$/g, "");
  const octets = hostname.split(".");
  const ipv4Loopback = octets.length === 4
    && octets.every((octet) => /^\d+$/.test(octet) && Number(octet) <= 255)
    && Number(octets[0]) === 127;
  const loopback = hostname === "localhost" || hostname === "::1" || ipv4Loopback;
  if (url.protocol !== "http:" || !loopback || !url.port || !["", "/"].includes(url.pathname) || url.search || url.hash) {
    throw new Error("本机中转代理必须是带端口的 HTTP 回环地址");
  }
}

function integerInRange(value: number, label: string, min: number, max: number): number {
  if (!Number.isInteger(value) || value < min || value > max) throw new Error(`${label}必须为 ${min}–${max} 的整数`);
  return value;
}

export function normalizeGoogleMaxBatchResponse(value: unknown): GoogleMaxBatchResponse {
  const outer = record(value);
  const source = record(outer.batch || outer);
  const outerItems = Array.isArray(outer.items) ? outer.items : [];
  const rawItems = Array.isArray(source.items) ? source.items : outerItems;
  const items = rawItems.map(normalizeGoogleMaxBatchItem);
  const rawCounts = record(source.counts);
  const counts = Object.fromEntries(
    Object.entries(rawCounts)
      .map(([key, count]) => [key.toLowerCase(), finiteNumber(count, 0)] as const),
  );
  const submitted = finiteNumber(source.submitted ?? source.total, items.length);
  return {
    batch: {
      id: String(source.id || source.batch_id || ""),
      status: String(source.status || "QUEUED").toUpperCase(),
      submitted,
      created_at: scalarDate(source.created_at),
      started_at: scalarDate(source.started_at),
      completed_at: scalarDate(source.completed_at),
      counts,
      items,
      error: optionalText(source.error),
    },
  };
}

function normalizeGoogleMaxBatchItem(value: unknown, index: number): GoogleMaxBatchItem {
  const item = record(value);
  return {
    account_ref: String(item.account_ref || item.ref || `account-${index + 1}`),
    status: String(item.status || "QUEUED").toUpperCase(),
    quota: optionalNumber(item.quota ?? item.free_quota),
    initialization_attempts: finiteNumber(item.initialization_attempts ?? item.init_attempts, 0),
    network_retries: finiteNumber(item.network_retries ?? item.network_attempts, 0),
    message: optionalText(item.message ?? item.error_message),
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function optionalText(value: unknown): string | null {
  return value === null || value === undefined || value === "" ? null : String(value);
}

function finiteNumber(value: unknown, fallback: number): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function scalarDate(value: unknown): string | number | null {
  return typeof value === "string" || typeof value === "number" ? value : null;
}

const TERMINAL_BATCH_STATUSES = new Set(["SUCCEEDED", "COMPLETED", "FAILED", "CANCELLED", "PARTIAL"]);
const TERMINAL_ITEM_STATUSES = new Set(["SUCCEEDED", "IMPORTED", "FAILED", "CANCELLED", "CHALLENGE_REQUIRED", "QUOTA_UNAVAILABLE"]);

export function isGoogleMaxBatchTerminal(batch?: GoogleMaxBatch | null): boolean {
  if (!batch) return false;
  if (TERMINAL_BATCH_STATUSES.has(batch.status.toUpperCase())) return true;
  return Boolean(batch.items.length) && batch.items.every((item) => TERMINAL_ITEM_STATUSES.has(item.status.toUpperCase()));
}

export function googleMaxBatchCounts(batch: GoogleMaxBatch): {
  total: number;
  active: number;
  succeeded: number;
  failed: number;
  imported: number;
} {
  const statuses = batch.items.map((item) => item.status.toUpperCase());
  const count = (...keys: string[]) => keys.reduce((sum, key) => sum + finiteNumber(batch.counts[key.toLowerCase()], 0), 0);
  const succeeded = count("succeeded", "success") || statuses.filter((status) => status === "SUCCEEDED" || status === "IMPORTED").length;
  const imported = count("imported") || statuses.filter((status) => status === "IMPORTED" || status === "SUCCEEDED").length;
  const failed = count("failed", "cancelled", "challenge_required", "quota_unavailable") || statuses.filter((status) => TERMINAL_ITEM_STATUSES.has(status) && status !== "SUCCEEDED" && status !== "IMPORTED").length;
  const total = batch.submitted || batch.items.length;
  const activeFromCounts = count("queued", "running", "retry_wait", "initializing", "checking_quota", "importing");
  return { total, active: activeFromCounts || Math.max(0, total - succeeded - failed), succeeded, failed, imported };
}
